import * as React from "react";
import {
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import ModalInc from "../../../../shared-ui/ModalInc";
import ButtonInc from "../../../../shared-ui/ButtonInc";

// Product-level MakerCheckTypeEnum (product-configuration's src/enums/index.ts).
// Kept in sync with the partner-side POLICY_MAKER_CHECK_TYPES constant
// (containers/PartnerStatus/constants.js): both mirror MakerCheckTypeEnum
// (AgentApproval | AmlApproval | PepApproval | IprsApproval).
const PRODUCT_POLICY_MAKER_CHECK_TYPES = [
  "AgentApproval",
  "AmlApproval",
  "PepApproval",
  "IprsApproval",
];

function buildFormState(check) {
  if (!check) {
    return {
      type: PRODUCT_POLICY_MAKER_CHECK_TYPES[0],
      label: "",
      sequence: "",
      allow_to_skip: false,
    };
  }
  return {
    type: check.type || PRODUCT_POLICY_MAKER_CHECK_TYPES[0],
    label: check.label || "",
    sequence: check.sequence != null ? String(check.sequence) : "",
    allow_to_skip: !!check.allow_to_skip,
  };
}

// Mirrors containers/PartnerStatus/components/Edit/AddEditPolicyMakerCheck.js,
// adapted for product-level policy_maker_checks: saves straight into local
// ProductForm state (no dedicated CRUD endpoint/save-in-flight states) since
// Product.policy_maker_checks is a plain embedded array on the whole-product DTO.
export default function AddEditProductPolicyMakerCheck(props) {
  var isEdit = !!props.existingCheck;
  var open = props.open;

  var [form, setForm] = React.useState(buildFormState(props.existingCheck));
  var [errors, setErrors] = React.useState({});

  React.useEffect(
    function() {
      if (open) {
        setForm(buildFormState(props.existingCheck));
        setErrors({});
      }
    },
    [open]
  );

  function set(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
    setErrors(function(prev) {
      if (!prev[field]) return prev;
      var next = Object.assign({}, prev);
      delete next[field];
      return next;
    });
  }

  // Returns the label of an existing check already using seqNum (excluding the
  // check currently being edited), or null when the sequence is free.
  function duplicateSequenceLabel(seqNum) {
    var existing = props.existingChecks || [];
    var currentId = props.existingCheck && props.existingCheck._local_id;
    for (var i = 0; i < existing.length; i++) {
      var c = existing[i];
      if (currentId && c._local_id === currentId) {
        continue;
      }
      if (Number(c.sequence) === seqNum) {
        return c.label || c.type || "#" + seqNum;
      }
    }
    return null;
  }

  function validate() {
    var errs = {};

    if (!form.type) {
      errs.type = "Type is required";
    }

    if (!form.label || !form.label.trim()) {
      errs.label = "Label is required";
    }

    if (form.sequence === "" || form.sequence == null) {
      errs.sequence = "Sequence is required";
    } else {
      var seqNum = Number(form.sequence);
      if (isNaN(seqNum) || seqNum < 0) {
        errs.sequence = "Sequence must be a non-negative number";
      } else {
        var dupLabel = duplicateSequenceLabel(seqNum);
        if (dupLabel) {
          errs.sequence =
            "Sequence " + seqNum + ' is already used by "' + dupLabel + '"';
        }
      }
    }

    return errs;
  }

  function handleSave() {
    var errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    var payload = {
      type: form.type,
      label: form.label.trim(),
      sequence: Number(form.sequence),
      allow_to_skip: !!form.allow_to_skip,
    };
    if (isEdit) {
      payload._local_id = props.existingCheck._local_id;
    }
    props.onSave(payload);
  }

  var modalTitle = isEdit
    ? "Edit Policy Maker Check: " + (props.existingCheck.label || "")
    : "Add Policy Maker Check";

  return (
    <ModalInc
      modalTitle={modalTitle}
      open={open}
      close={props.onClose}
      fullWidth
      maxWidth="sm"
    >
      <Grid container spacing={3} style={{ marginTop: 4 }}>
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Type"
            value={form.type}
            onChange={function(e) {
              set("type", e.target.value);
            }}
            variant="outlined"
            error={!!errors.type}
            helperText={errors.type || "Approval type for this maker-checker step."}
          >
            {PRODUCT_POLICY_MAKER_CHECK_TYPES.map(function(t) {
              return (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Label"
            value={form.label}
            onChange={function(e) {
              set("label", e.target.value);
            }}
            variant="outlined"
            error={!!errors.label}
            helperText={errors.label || "Descriptive label for this approval step."}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            type="number"
            label="Sequence"
            value={form.sequence}
            onChange={function(e) {
              set("sequence", e.target.value);
            }}
            variant="outlined"
            error={!!errors.sequence}
            helperText={
              errors.sequence || "Execution order of this step (must be unique)."
            }
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.allow_to_skip}
                onChange={function(e) {
                  set("allow_to_skip", e.target.checked);
                }}
                color="primary"
              />
            }
            label="Allow this step to be skipped"
          />
        </Grid>

        <Grid item xs={12}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <ButtonInc
              variant="contained"
              color="primary"
              size="large"
              btnType="secondary"
              onClick={props.onClose}
            >
              Cancel
            </ButtonInc>
            <ButtonInc
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
            >
              {isEdit ? "Save Changes" : "Add Check"}
            </ButtonInc>
          </div>
        </Grid>
      </Grid>
    </ModalInc>
  );
}
